import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

const useGoogleOAuth = (domainId, onSuccess) => {
  const [oauthLoading, setOauthLoading] = useState(false);
  const popupCheckIntervalRef = useRef(null);
  const messageListenerRef = useRef(null);

  const initiateOAuth = useCallback(async () => {
    if (!domainId) return;
    
    setOauthLoading(true);
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/google-calendar/auth`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res?.data?.result?.auth_url) {
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          res.data.result.auth_url,
          'google_calendar_auth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes`
        );
        
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setOauthLoading(false);
          return { success: false, needsPopupHelp: true };
        }

        const handleMessage = (event) => {
          const validOrigins = [
            'http://localhost:8000',
            'http://localhost:3000',
            'https://api.ansera.ai',
          ];
          
          if (!validOrigins.includes(event.origin)) return;
          
          if (event.data.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
            if (messageListenerRef.current) {
              window.removeEventListener('message', messageListenerRef.current);
              messageListenerRef.current = null;
            }
            
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
            
            if (popup && !popup.closed) {
              popup.close();
            }
            
            setOauthLoading(false);
            onSuccess?.();
            return { success: true };
          } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
            setOauthLoading(false);
            return { success: false, message: event.data.message };
          }
        };
        
        messageListenerRef.current = handleMessage;
        window.addEventListener('message', messageListenerRef.current);
        
        popupCheckIntervalRef.current = setInterval(() => {
          try {
            if (popup.closed) {
              if (popupCheckIntervalRef.current) {
                clearInterval(popupCheckIntervalRef.current);
                popupCheckIntervalRef.current = null;
              }
              
              if (messageListenerRef.current) {
                window.removeEventListener('message', messageListenerRef.current);
                messageListenerRef.current = null;
              }
              
              if (oauthLoading) {
                setOauthLoading(false);
              }
            }
          } catch (e) {
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
          }
        }, 1000);
        
        return { success: true };
      } else {
        throw new Error("No auth URL received");
      }
    } catch (err) {
      console.error("Failed to initiate Google auth:", err);
      setOauthLoading(false);
      return { success: false, message: "Failed to connect to Google Calendar." };
    }
  }, [domainId, onSuccess]);

  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
      
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
    };
  }, []);

  return {
    oauthLoading,
    initiateOAuth
  };
};

export default useGoogleOAuth;